import { Component, Input, OnInit, OnChanges } from '@angular/core';
import { ModalController, AlertController } from '@ionic/angular';
import { SessionService } from 'src/app/services/session.service';
import { AnimationController } from '@ionic/angular';
import { AccountService } from 'src/app/services/account.service';
import { SectionService } from 'src/app/services/section.service';
import { AlertService } from 'src/app/services/alert.service';
import { Router } from '@angular/router';

@Component({
  selector: 'app-account-select-modal',
  templateUrl: './account-select-modal.component.html',
  styleUrls: ['./account-select-modal.component.scss']
})
export class AccountSelectModalComponent implements OnInit, OnChanges {
  @Input() accounts: any[] = [];
  @Input() isManagingOrganizations: boolean = false;
  @Input() mode: 'organization' | 'account' = 'organization'; // Modo: criar organização ou conta
  @Input() sectionId: number | null = null; // ID da organização para vincular a conta
  @Input() isOnboarding: boolean = false; // Indica se está no fluxo de onboarding
  @Input() onboardingMessage: string = ''; // Mensagem customizada para o onboarding
  selectedAccountId: number | null = null;
  private _searchTerm: string = '';
  filteredAccounts: any[] = [];
  isCreating: boolean = false;
  hasEdits: boolean = false; // Flag para indicar que houve edições (renomear/excluir)
  private lastCreationAttempt: number = 0;
  private readonly DEBOUNCE_TIME_MS = 2000; // 2 segundos entre tentativas

  get searchTerm(): string {
    return this._searchTerm;
  }

  set searchTerm(value: string) {
    this._searchTerm = value;
    this.filterAccounts();
  }

  constructor(
    private modalCtrl: ModalController,
    private sessionService: SessionService,
    private animationCtrl: AnimationController,
    private accountService: AccountService,
    private sectionService: SectionService,
    private alertService: AlertService,
    private alertController: AlertController,
    private router: Router
  ) {}

  ngOnInit() {
    // Inicializa o ID selecionado baseado no modo
    if (this.mode === 'organization') {
      // Modo organização: usa o sectionId atual
      this.selectedAccountId = this.sessionService.sectionId || null;
    } else {
      // Modo conta: usa o accountId atual
      this.selectedAccountId = this.sessionService.accountId || null;
    }
    this.updateFilteredAccounts();
  }

  ngOnChanges() {
    // Atualiza filteredAccounts quando accounts mudar
    this.updateFilteredAccounts();
  }

  private updateFilteredAccounts() {
    if (this.accounts && this.accounts.length > 0) {
      this.filteredAccounts = [...this.accounts];
    } else {
      this.filteredAccounts = [];
    }
  }

  filterAccounts() {
    if (!this.searchTerm || this.searchTerm.trim() === '') {
      this.updateFilteredAccounts();
    } else {
      const searchLower = this.searchTerm.toLowerCase().trim();
      this.filteredAccounts = this.accounts.filter(account => 
        account.name.toLowerCase().includes(searchLower)
      );
    }
  }

  // Getter para labels dinâmicos baseado no modo
  get entityLabel(): string {
    return this.mode === 'account' ? 'conta' : 'organização';
  }

  get entityLabelCapitalized(): string {
    return this.mode === 'account' ? 'Conta' : 'Organização';
  }

  async createAccount() {
    // Rate limiting: evita múltiplas tentativas rápidas
    const now = Date.now();
    const timeSinceLastAttempt = now - this.lastCreationAttempt;

    if (timeSinceLastAttempt < this.DEBOUNCE_TIME_MS && this.lastCreationAttempt > 0) {
      const remainingTime = Math.ceil((this.DEBOUNCE_TIME_MS - timeSinceLastAttempt) / 1000);
      await this.alertService.error(`Aguarde ${remainingTime} segundo(s) antes de criar outra ${this.entityLabel}`);
      return;
    }

    this.lastCreationAttempt = now;

    let accountName = '';

    // Validação: campo não pode estar vazio
    if (!this.searchTerm || this.searchTerm.trim().length === 0) {
      await this.alertService.error(`Por favor, digite o nome da ${this.entityLabel}`);
      return;
    }

    accountName = this.searchTerm;

    // Sanitiza o nome removendo caracteres perigosos
    accountName = this.sanitizeAccountName(accountName);

    // Validação: Nome não pode estar vazio após sanitização
    if (accountName.trim().length === 0) {
      await this.alertService.error(`Nome da ${this.entityLabel} é obrigatório`);
      return;
    }

    // Validação: Tamanho mínimo
    if (accountName.trim().length < 3) {
      await this.alertService.error('Nome deve ter no mínimo 3 caracteres');
      return;
    }

    // Validação: Tamanho máximo
    if (accountName.trim().length > 100) {
      await this.alertService.error('Nome deve ter no máximo 100 caracteres');
      return;
    }

    // Validação: Verifica tags HTML perigosas
    if (/<script|<iframe|<object|<embed/i.test(accountName)) {
      await this.alertService.error('Nome contém código inválido');
      return;
    }

    const trimmedName = accountName.trim();

    // Verifica se já existe uma conta com esse nome (case-insensitive)
    const existingAccount = this.accounts.find(acc =>
      acc.name.toLowerCase() === trimmedName.toLowerCase()
    );

    if (existingAccount) {
      await this.alertService.error(`Já existe uma ${this.entityLabel} com este nome`);
      return;
    }

    this.isCreating = true;

    try {
      let newEntity: any;

      if (this.mode === 'organization') {
        // Criando uma nova organização (Section)
        // Todas as organizações são filhas diretas da Section 1 (root)
        const parentSection = 1;

        const sectionData: any = {
          name: trimmedName,
          ownerId: this.sessionService.user.id,
          sectionId: parentSection  // Parent section: sempre Section 1 (root)
        };

        // Usa createOrganization que cria Section + Client + Membership + MembershipRole
        newEntity = await this.sectionService.createOrganization(sectionData);

        // Adiciona à lista local
        this.accounts.push(newEntity);
        this.filteredAccounts = [...this.accounts];

        // Limpa o campo de busca
        this.searchTerm = '';

        // Marca que houve mudanças
        this.hasEdits = true;

        // Fecha o modal com a nova organização
        this.modalCtrl.dismiss({ data: newEntity, isOrganizationCreated: true, hasEdits: true });

        await this.alertService.successToast(`${this.entityLabelCapitalized} criada com sucesso!`);

        // O modal será fechado e o header componente cuidará da troca de organização automaticamente
        // (sem refresh de página)

      } else {
        // Criando uma nova conta (Account) vinculada à organização

        // Usa o sectionId passado como prop, ou fallback para sessionService
        const targetSectionId = this.sectionId || this.sessionService.sectionId;

        const accountData: any = {
          name: trimmedName,
          userId: this.sessionService.user.id,
          sectionId: targetSectionId,
          section_id: targetSectionId,
          status: 'active'
        };


        newEntity = await this.accountService.create(accountData);

        // Adiciona a nova conta à lista local
        this.accounts.push(newEntity);
        this.filteredAccounts = [...this.accounts];

        // Limpa o campo de busca
        this.searchTerm = '';

        // ✅ IMPORTANTE: Atualiza a sessão ANTES de fechar o modal
        this.sessionService.account = newEntity;
        this.sessionService.accountId = newEntity.id;
        console.log('✅ sessionService.accountId definido:', this.sessionService.accountId);

        await this.alertService.successToast(`${this.entityLabelCapitalized} criada com sucesso!`);

        // Marca que houve mudanças
        this.hasEdits = true;

        // Fecha o modal passando os dados da nova conta
        this.modalCtrl.dismiss({ data: newEntity, isAccountCreated: true, hasEdits: true });

        // Só redireciona para o dashboard se NÃO estiver no modo onboarding
        if (!this.isOnboarding) {
          setTimeout(async () => {
            await this.router.navigate(['/dashboard', newEntity.id]);
          }, 500);
        }
      }

    } catch (error: any) {
      console.error('Erro ao criar conta:', error);

      // Tratamento de erros específicos por código HTTP
      const statusCode = error?.status || error?.response?.status;
      let errorMessage = `Erro ao criar ${this.entityLabel}. Tente novamente.`;

      switch (statusCode) {
        case 409:
          errorMessage = `Já existe uma ${this.entityLabel} com este nome no sistema.`;
          break;
        case 400:
          errorMessage = `Dados inválidos. Verifique o nome da ${this.entityLabel}.`;
          break;
        case 401:
          errorMessage = 'Sessão expirada. Faça login novamente.';
          await this.sessionService.logout();
          break;
        case 403:
          errorMessage = `Você não tem permissão para criar ${this.entityLabel}s.`;
          break;
        case 500:
          errorMessage = 'Erro no servidor. Tente novamente mais tarde.';
          break;
      }

      await this.alertService.error(errorMessage);
    } finally {
      this.isCreating = false;
    }
  }

  /**
   * Sanitiza o nome da conta removendo caracteres perigosos
   */
  private sanitizeAccountName(name: string): string {
    if (!name) return '';

    // Remove espaços extras no início e fim
    let sanitized = name.trim();

    // Remove múltiplos espaços consecutivos
    sanitized = sanitized.replace(/\s+/g, ' ');

    // Remove apenas tags HTML perigosas
    sanitized = sanitized.replace(/[<>]/g, '');

    return sanitized;
  }

  async logout() {
    await this.modalCtrl.dismiss(null, 'logout');
    await this.sessionService.logout();
  }

  dismiss() {
    // Animação de saída
    const animation = this.animationCtrl.create()
      .addElement(document.querySelector('.modal-wrapper')!)
      .duration(300)
      .fromTo('opacity', '1', '0')
      .fromTo('transform', 'translateY(0)', 'translateY(20px)');
    
    animation.play();
    
    setTimeout(() => {
      this.modalCtrl.dismiss(this.hasEdits ? { hasEdits: true } : null);
    }, 250);
  }

  selectAccount(account: any) {
    // Atualiza o ID selecionado (para efeito visual imediato)
    this.selectedAccountId = account.id;
    
    // Pequeno atraso visual para mostrar a seleção antes de fechar o modal
    setTimeout(() => {
      this.modalCtrl.dismiss(account);
    }, 300);
  }

  selectOrganization(account: any) {
    // Quando em modo "Gerenciar Organizações", seleciona e retorna a organização
    this.selectedAccountId = account.id;
    
    // Retorna a organização selecionada para que o header atualize a conta
    setTimeout(() => {
      this.modalCtrl.dismiss({ data: account, isOrganizationSelected: true });
    }, 300);
  }

  openAddOrganizationModal() {
    // Ativa o modo de criação de organização
    // O usuário pode digitar no searchbar e clicar em "Criar"
    const searchInput = document.querySelector('.search-input') as any;
    if (searchInput) {
      searchInput.setFocus();
    }
  }

  async editAccount(account: any, event?: Event) {
    if (event) {
      event.stopPropagation();
    }

    const alert = await this.alertController.create({
      header: `Renomear ${this.entityLabelCapitalized}`,
      inputs: [
        {
          name: 'name',
          type: 'text',
          value: account.name,
          placeholder: `Nome da ${this.entityLabel}`
        }
      ],
      buttons: [
        { text: 'Cancelar', role: 'cancel' },
        {
          text: 'Salvar',
          handler: async (data) => {
            const newName = this.sanitizeAccountName(data.name);

            if (!newName || newName.length === 0) {
              this.alertService.error('Nome é obrigatório');
              return false;
            }

            if (newName.length < 3) {
              this.alertService.error('Nome deve ter no mínimo 3 caracteres');
              return false;
            }

            if (newName.length > 100) {
              this.alertService.error('Nome deve ter no máximo 100 caracteres');
              return false;
            }

            if (/<script|<iframe|<object|<embed/i.test(newName)) {
              this.alertService.error('Nome contém código inválido');
              return false;
            }

            // Verifica duplicatas (excluindo o próprio item)
            const duplicate = this.accounts.find(acc =>
              acc.id !== account.id && acc.name.toLowerCase() === newName.toLowerCase()
            );
            if (duplicate) {
              this.alertService.error(`Já existe uma ${this.entityLabel} com este nome`);
              return false;
            }

            // Se o nome não mudou, não faz nada
            if (newName === account.name) {
              return true;
            }

            try {
              if (this.mode === 'organization') {
                await this.sectionService.renameOrganization(account.id, newName);
              } else {
                await this.accountService.update({ id: account.id, name: newName });
              }

              // Atualiza localmente
              account.name = newName;
              this.filterAccounts();

              // Se for o item selecionado, atualiza a sessão
              if (this.mode === 'account' && account.id === this.sessionService.accountId) {
                this.sessionService.account = { ...this.sessionService.account, name: newName };
              }

              this.hasEdits = true;
              await this.alertService.successToast(`${this.entityLabelCapitalized} renomeada com sucesso!`);
            } catch (error: any) {
              console.error('Erro ao renomear:', error);
              const statusCode = error?.status || error?.response?.status;
              let errorMessage = `Erro ao renomear ${this.entityLabel}. Tente novamente.`;

              switch (statusCode) {
                case 401:
                  errorMessage = 'Sessão expirada. Faça login novamente.';
                  await this.sessionService.logout();
                  break;
                case 403:
                  errorMessage = `Você não tem permissão para renomear esta ${this.entityLabel}.`;
                  break;
                case 404:
                  errorMessage = `${this.entityLabelCapitalized} não encontrada.`;
                  break;
                case 409:
                  errorMessage = `Já existe uma ${this.entityLabel} com este nome no sistema.`;
                  break;
                case 500:
                  errorMessage = 'Erro no servidor. Tente novamente mais tarde.';
                  break;
              }

              await this.alertService.error(errorMessage);
              return false;
            }

            return true;
          }
        }
      ]
    });

    await alert.present();
  }

  async deleteAccount(account: any, event?: Event) {
    if (event) {
      event.stopPropagation();
    }

    let warningMsg = '';
    let accountToSwitchTo: any = null;

    // Se estiver tentando excluir a entidade logada atualmente
    if ((this.mode === 'account' && this.sessionService.accountId === account.id) ||
        (this.mode === 'organization' && this.sessionService.sectionId === account.id)) {
      const alternatives = this.accounts.filter(a => a.id !== account.id);
      if (alternatives.length === 0) {
        await this.alertService.error(`Não é possível excluir a ${this.entityLabel} atual pois é a única ${this.entityLabel} que você possui.`);
        return;
      }
      accountToSwitchTo = alternatives[0];
      warningMsg = `Esta é a sua ${this.entityLabel} atual. Você será automaticamente movido para "<strong>${accountToSwitchTo.name}</strong>".<br><br>`;
    }

    // Confirmação de exclusão
    const alert = await this.alertController.create({
      header: 'Confirmar Exclusão',
      message: `${warningMsg}Deseja realmente excluir a ${this.entityLabel} <strong>${account.name}</strong>?<br><br>Esta ação não pode ser desfeita e todos os dados relacionados serão removidos.`,
      buttons: [
        {
          text: 'Cancelar',
          role: 'cancel'
        },
        {
          text: 'Excluir',
          cssClass: 'alert-button-danger',
          handler: async () => {
            await this.performDelete(account, accountToSwitchTo);
          }
        }
      ]
    });

    await alert.present();
  }

  private async performDelete(account: any, accountToSwitchTo?: any) {
    try {
      // Exclui baseado no modo: organização (Section) ou conta (Account)
      if (this.mode === 'organization') {
        // Excluir conta a conta associada a esta organização antes da organização em si para garantir
        const acctsToDelete = await this.accountService.listAll();
        if (acctsToDelete && Array.isArray(acctsToDelete)) {
          const orgAccounts = acctsToDelete.filter(a => a.sectionId === account.id);
          for (const acc of orgAccounts) {
            try {
              await this.accountService.delete(acc.id);
            } catch (e) {
              console.error('Erro ao excluir conta atrelada à org', e);
            }
          }
        }
        await this.sectionService.deleteOrganization(account.id);
      } else {
        await this.accountService.delete(account.id);
      }

      // Remove da lista local
      this.accounts = this.accounts.filter(acc => acc.id !== account.id);
      this.filterAccounts();
      this.hasEdits = true;

      // Se apagou a conta/org atual, muda a sessão para a alternativa
      if (accountToSwitchTo) {
        if (this.mode === 'account') {
          this.sessionService.account = accountToSwitchTo;
          this.sessionService.accountId = accountToSwitchTo.id;
          localStorage.setItem('selectedAccountId', String(accountToSwitchTo.id));
          this.sessionService.accountChanged.emit();
          this.selectedAccountId = accountToSwitchTo.id;
          await this.router.navigate(['/dashboard', accountToSwitchTo.id]);
        } else {
          // Quando for organização, para carregar as contas corretamente e direcionar para o novo local
          // Fechamos o modal indicando e delegamos para o header component o switch completo
          await this.alertService.successToast(`${this.entityLabelCapitalized} excluída com sucesso!`);
          this.modalCtrl.dismiss({ data: accountToSwitchTo, isOrganizationSelected: true, hasEdits: true });
          return;
        }
      }

      await this.alertService.successToast(`${this.entityLabelCapitalized} excluída com sucesso!`);

    } catch (error: any) {
      console.error('Erro ao excluir conta:', error);

      const statusCode = error?.status || error?.response?.status;
      let errorMessage = `Erro ao excluir ${this.entityLabel}. Tente novamente.`;

      switch (statusCode) {
        case 400:
          errorMessage = `Não é possível excluir esta ${this.entityLabel}. Verifique se não há dados vinculados.`;
          break;
        case 401:
          errorMessage = 'Sessão expirada. Faça login novamente.';
          await this.sessionService.logout();
          break;
        case 403:
          errorMessage = `Você não tem permissão para excluir esta ${this.entityLabel}.`;
          break;
        case 404:
          errorMessage = `${this.entityLabelCapitalized} não encontrada.`;
          break;
        case 409:
          errorMessage = `Não é possível excluir esta ${this.entityLabel} pois existem dados vinculados a ela.`;
          break;
        case 500:
          errorMessage = 'Erro no servidor. Tente novamente mais tarde.';
          break;
      }

      await this.alertService.error(errorMessage);
    }
  }
}